import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../../stores/authStore'

interface ChatImageProps {
  src: string
  alt?: string
}

/**
 * Image component that loads images with authentication token.
 * Similar to AudioPlayer's blob URL approach.
 */
export default function ChatImage({ src, alt = 'Изображение' }: ChatImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const blobUrlRef = useRef<string | null>(null)
  const accessToken = useAuthStore((state) => state.accessToken)

  // If src is a blob or data URL, use directly (optimistic UI)
  useEffect(() => {
    if (src && (src.startsWith('blob:') || src.startsWith('data:'))) {
      setBlobUrl(src)
      setIsLoading(false)
      setError(null)
    }
  }, [src])

  useEffect(() => {
    if (!src || !accessToken) return
    // Skip fetch for blob/data URLs — already handled above
    if (src.startsWith('blob:') || src.startsWith('data:')) return

    let cancelled = false
    const controller = new AbortController()

    // Revoke previous blob URL if exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }

    const fetchImage = async (token: string, isRetry = false) => {
      setIsLoading(true)
      setError(null)

      try {
        const apiUrl = import.meta.env.VITE_API_URL || ''
        const fullUrl = src.startsWith('/') ? `${apiUrl}${src}` : src

        const response = await fetch(fullUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        })

        // If 401 and not already retrying, try to refresh token
        if (response.status === 401 && !isRetry) {
          const refreshToken = useAuthStore.getState().refreshToken
          if (refreshToken) {
            try {
              const refreshResponse = await fetch(`${apiUrl}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
              })
              if (refreshResponse.ok) {
                const { access_token, refresh_token } = await refreshResponse.json()
                useAuthStore.getState().updateTokens(access_token, refresh_token)
                // Retry with new token
                return fetchImage(access_token, true)
              }
            } catch {
              // Refresh failed, fall through to error
            }
          }
        }

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`)
        }

        const blob = await response.blob()
        if (!cancelled) {
          const url = URL.createObjectURL(blob)
          blobUrlRef.current = url
          setBlobUrl(url)
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled && err instanceof Error && err.name !== 'AbortError') {
          console.error('Image fetch error:', err)
          setError(err.message)
          setIsLoading(false)
        }
      }
    }

    fetchImage(accessToken)

    return () => {
      cancelled = true
      controller.abort()
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [src, accessToken])

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full max-w-xs h-32 bg-neutral-100 rounded-xl flex items-center justify-center">
        <svg className="w-6 h-6 animate-spin text-neutral-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="w-full max-w-xs h-24 bg-red-50 rounded-xl flex items-center justify-center gap-2 text-red-500">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm">Ошибка загрузки</span>
      </div>
    )
  }

  return (
    <>
      {/* Image thumbnail */}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          className="max-w-xs max-h-48 rounded-xl cursor-pointer hover:opacity-90 transition-opacity object-cover"
          onClick={() => setIsFullscreen(true)}
        />
      )}

      {/* Fullscreen modal */}
      {isFullscreen && blobUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
            onClick={() => setIsFullscreen(false)}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={blobUrl}
            alt={alt}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}
